import { useCallback, useMemo } from 'react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { usePUR5010_R01Store } from './Store_PUR5010_R01';
import { PUR5010_R01TabType } from './Store_PUR5010_R01';
import { getItemStockColumns, getWarehouseStockColumns, getBrandStockColumns } from './components/ColumnDefinitions';
import {  ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { exportPUR5010_R01Excel,getPUR5010_R01Data } from './Api_PUR5010_R01';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { getUniqueRowKey, PUR5010_R01_TABS } from './utils/tabConfig';

// 훅으로 전환: 컨테이너에서 정상적인 훅 호출로 핸들러를 수급
interface WMS2010ContentProps {
    setMessage: (message: string) => void;
}

export const Content_PUR5010_R01 = (props: WMS2010ContentProps) => {
    const { setMessage } = props;
	const { unitList } = useCommonCodeStore();
    
    // 권한 체크
    useFetchActionbarAuth('PUR5010_R01');

    const {
		PUR5010_R01Filter,
		PUR5010_R01activeTab,
        setPUR5010_R01ActiveTab, 
		PUR5010_R01tabData, 
        setPUR5010_R01TabData,
		setPUR5010_R01TabSelectedIds,
		setPUR5010_R01TabLoadingState,
		resetAllPUR5010_R01TabScrollTop,
		setPUR5010_R01TabScrollTop,
	} = usePUR5010_R01Store();

    // 활성 탭에 따른 데이터/컬럼 매핑 
    const { columns, data } = useMemo(() => {
        switch (PUR5010_R01activeTab) {
            case 'item':
                return { columns: getItemStockColumns(unitList), data: PUR5010_R01tabData.item };
            case 'warehouse':
                return { columns: getWarehouseStockColumns(unitList), data: PUR5010_R01tabData.warehouse };
            case 'brand':
                return { columns: getBrandStockColumns(unitList), data: PUR5010_R01tabData.brand };
            default:
                return { columns: getItemStockColumns(unitList), data: PUR5010_R01tabData.item };
        }
    }, [PUR5010_R01activeTab, PUR5010_R01tabData.item, PUR5010_R01tabData.warehouse, PUR5010_R01tabData.brand, unitList]);

    // 뷰 TabType 선언 
    const getViewType = useCallback((tab: PUR5010_R01TabType): string => {
        switch (tab) {
            case 'item': return 'item';
            case 'warehouse': return 'warehouse';
            case 'brand': return 'brand';
            default: return 'item';
        }
    }, []);

    // 탭 이름 변환 함수 (파일명/메시지용)
    const getTabDisplayName = useCallback((tab: PUR5010_R01TabType): string => {
        return PUR5010_R01_TABS.find((t) => t.id === tab)?.label ?? '품목별 재고';
    }, []);

    // 탭 변경 시 상태 초기화 
    const changeTabAndFetch = useCallback((tab: PUR5010_R01TabType, options: { clearData?: boolean; clearSelection?: boolean } = {}) => {
        const { clearData = true, clearSelection = true } = options;
        
        // 활성 탭 변경
        setPUR5010_R01ActiveTab(tab);
        
        // 데이터 및 선택 상태 초기화
        if (clearData) {
            setPUR5010_R01TabData(tab, []);
        }
        if (clearSelection) {
            setPUR5010_R01TabSelectedIds(tab, []);
        }
    }, [setPUR5010_R01ActiveTab, setPUR5010_R01TabData, setPUR5010_R01TabSelectedIds]);

    // 전체 탭 병렬 조회 
    const fetchData = useCallback(async () => {
        resetAllPUR5010_R01TabScrollTop();
        const tabs: PUR5010_R01TabType[] = ['item', 'warehouse', 'brand'];

        // 각 탭별 응답 건수를 저장할 배열
        const tabCounts: { tab: PUR5010_R01TabType; count: number }[] = [];

        await Promise.all(tabs.map(async (tab) => {
            try {
                setPUR5010_R01TabLoadingState(tab, true);
                const viewType = getViewType(tab);
                const res = await getPUR5010_R01Data(viewType, PUR5010_R01Filter);
                // 데이터 검증 및 중복 제거 (행 ID 기준) - 생산실적 규격
                if (res?.data?.data && Array.isArray(res.data.data)) { // res.data.data 의 값이 존재하고 인경우 (배열인경우) 
                    const validData = res.data.data.filter((item: any) => item && typeof item === 'object');// item(매개변수)이고 item 타입이 object 인경우 validData 에 저장
                    const uniqueData = validData.filter((item: any, index: number, array: any[]) => {// validData 배열에서 중복된 항목을 제거하여 uniqueData에 저장
                        const rowId = getUniqueRowKey(tab, item);
                        // 배열의 인덱스를 찾아서 findIndex를 활용한 중복 제거 로직을 통과한 고유한 데이터만 uniqueData에 담습니다. 
                        const isFirst = array.findIndex((i: any) => {
                            const compareId = getUniqueRowKey(tab, i);
                            return compareId === rowId;
                        }) === index;
                        return isFirst;
                    });

                    if (uniqueData.length > 0) {
                        setPUR5010_R01TabData(tab, uniqueData);
                        tabCounts.push({ tab, count: uniqueData.length });
                    } else {
                        console.warn(`${tab} 탭: 처리 후 유효한 데이터가 없습니다.`);
                        setPUR5010_R01TabData(tab, []);
                        tabCounts.push({ tab, count: 0 });
                    }
                } else {
                    console.warn(`${tab} 탭: 응답 데이터가 없거나 잘못된 형식입니다.`, res);
                    setPUR5010_R01TabData(tab, []);
                    tabCounts.push({ tab, count: 0 });
                }
            } catch (e) {
                console.error(`${tab} 탭 데이터 조회 실패:`, e);
                setPUR5010_R01TabData(tab, []);
                tabCounts.push({ tab, count: 0 });
            } finally {
                setPUR5010_R01TabLoadingState(tab, false);
            }
        }));
        
        // 각 탭별 건수 표시
        const totalCount = tabCounts.reduce((sum, item) => sum + item.count, 0);
        
        if (totalCount > 0) {
            const tabDetails = tabCounts.map(item => `${getTabDisplayName(item.tab)}: ${item.count}건`).join(', ');
            ShowSuccessFetch(setMessage, `재고 조회가 완료되었습니다. (총 ${totalCount}건) - ${tabDetails}`);
            setTimeout(() => setMessage(""), 5000);
        } else {
            showWarning('데이터가 없습니다.');
        }
    }, [getViewType, setPUR5010_R01TabData, setPUR5010_R01TabLoadingState, PUR5010_R01Filter, getTabDisplayName, setMessage, resetAllPUR5010_R01TabScrollTop]);

    // 개별 탭 조회 함수
    const fetchSingleTabData = useCallback(async (tab: PUR5010_R01TabType) => {
        setPUR5010_R01TabScrollTop(tab, null);
        try {
            setPUR5010_R01TabLoadingState(tab, true);

            const viewType = getViewType(tab);
            const res: any = await getPUR5010_R01Data(viewType, PUR5010_R01Filter);

            // 데이터 검증 및 처리
            if (res?.data?.data && Array.isArray(res.data.data)) {
                const validData = res.data.data.filter((item: any) => item && typeof item === 'object');

                // 중복 제거 (행 ID 기준)
                const uniqueData = validData.filter((item: any, index: number, array: any[]) => {
                    const rowId = getUniqueRowKey(tab, item);

                    const isFirst = array.findIndex((i: any) => {
                        const compareId = getUniqueRowKey(tab, i);
                        return compareId === rowId;
                    }) === index;
                    return isFirst;
                });

                if (uniqueData.length > 0) {
                    setPUR5010_R01TabData(tab, uniqueData);
					ShowSuccessFetch(setMessage, `${getTabDisplayName(tab)} 조회가 완료되었습니다. (총 ${uniqueData.length}건)`);
					setTimeout(() => setMessage(""), 5000);
                } else {
                    console.warn(`${tab} 탭: 처리 후 유효한 데이터가 없습니다.`);
                    setPUR5010_R01TabData(tab, []);
                    showWarning(`${getTabDisplayName(tab)} 데이터가 없습니다.`);
                }
            } else {
                console.warn(`${tab} 탭: API 응답 데이터가 없거나 잘못된 형식입니다.`, res);
                setPUR5010_R01TabData(tab, []);
                showWarning(`${getTabDisplayName(tab)} 데이터가 없습니다.`);
            }
        } catch (e) {
            console.error(`${tab} 탭 데이터 조회 실패:`, e);
            setPUR5010_R01TabData(tab, []);
            setMessage(`${getTabDisplayName(tab)} 조회 중 오류가 발생했습니다.`);
            showWarning(`${getTabDisplayName(tab)} 조회 중 오류가 발생했습니다.`);
        } finally {
            setPUR5010_R01TabLoadingState(tab, false);
        }
    }, [getViewType, setPUR5010_R01TabData, setPUR5010_R01TabLoadingState, PUR5010_R01Filter, getTabDisplayName, setMessage, setPUR5010_R01TabScrollTop]);

    // 탭 변경 시 데이터 조회 처리 - 항상 조회
    const handleTabChange = useCallback(async (tab: PUR5010_R01TabType) => {
        try {
            // 1. 탭 변경 및 상태 초기화
            changeTabAndFetch(tab, { clearData: true, clearSelection: true });
            
            // 2. 활성 탭 항상 조회 (데이터가 있어도 최신 데이터로 갱신)
            await fetchSingleTabData(tab);
        } catch (error) {
            console.error('탭 변경 중 오류:', error);
        }
    }, [changeTabAndFetch, fetchSingleTabData]);

    // Excel 내보내기 핸들러
    const handleExcelClick = useCallback(async () => {
        try {
            const state = usePUR5010_R01Store.getState();
            const activeTab = state.PUR5010_R01activeTab;
            const filter = state.PUR5010_R01Filter;

            // 뷰 타입 변환
            const viewType = getViewType(activeTab);
            // API 호출
            const response = await exportPUR5010_R01Excel(viewType, { ...filter });

            // Base64 응답 처리
            if (response?.data?.data) {
                // 공통 유틸리티 사용
                downloadSettingExcel({ 
                    responseData: response.data.data, 
                    fileName: `재고조회_${getTabDisplayName(activeTab)}.xlsx`
                });
            } else {
                throw new Error('서버에서 올바른 엑셀 데이터를 받지 못했습니다.');
            }
            
        } catch (error) {
            console.error('Excel 내보내기 실패:', error);
            
            // 사용자에게 에러 알림
            if (error instanceof Error) {
                showWarning(`엑셀 내보내기 실패: ${error.message}`);
            } else {
                showWarning('엑셀 내보내기 중 오류가 발생했습니다.');
            }
        }
    }, [getViewType, getTabDisplayName]);

    return { 
        fetchData, 
        handleExcelClick, 
        handleTabChange,
        columns,
        data
    };
};
